'use client'

/**
 * WhatThisMeansCard Component
 *
 * Provides plain-language interpretations of technical calculator results.
 * Designed to help first-time users understand what their results actually mean
 * and what actions they should take.
 *
 * Usage:
 *   <WhatThisMeansCard
 *     interpretation="Your lawn needs about 1.2 inches of water per week..."
 *     tips={['Water early morning to reduce evaporation', 'Split into 2-3 sessions']}
 *   />
 */

import * as React from 'react'
import { Card } from '@/components/ui/card'
import { Info, CheckCircle2, AlertTriangle, Lightbulb } from 'lucide-react'
import { cn } from '@/lib/utils'
import { WhatThisMeansConfig } from './types'

interface WhatThisMeansCardProps extends WhatThisMeansConfig {
  /** Additional CSS classes */
  className?: string
  /** Show a light bulb icon for tips section */
  showTipsIcon?: boolean
}

const variantStyles = {
  info: {
    background: 'bg-gray-50',
    border: 'border-gray-200',
    iconBg: 'bg-gray-100',
    icon: 'text-gray-600',
    title: 'text-gray-800',
    Icon: Info,
  },
  success: {
    background: 'bg-green-50',
    border: 'border-green-200',
    iconBg: 'bg-green-100',
    icon: 'text-green-600',
    title: 'text-green-800',
    Icon: CheckCircle2,
  },
  warning: {
    background: 'bg-amber-50',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
    icon: 'text-amber-600',
    title: 'text-amber-800',
    Icon: AlertTriangle,
  },
}

/**
 * Interpretive card that explains calculator results in plain language.
 * Supports info, success, and warning variants with optional actionable tips.
 */
export function WhatThisMeansCard({
  interpretation,
  tips,
  variant = 'info',
  title = 'What This Means',
  className,
  showTipsIcon = true,
}: WhatThisMeansCardProps) {
  const styles = variantStyles[variant]
  const Icon = styles.Icon

  return (
    <Card
      className={cn(
        'overflow-hidden border',
        styles.border,
        styles.background,
        className
      )}
    >
      <div className="p-4 space-y-3">
        {/* Header with icon */}
        <div className="flex items-center gap-3">
          <div className={cn('p-2 rounded-lg', styles.iconBg)}>
            <Icon
              className={cn('h-5 w-5', styles.icon)}
              aria-hidden="true"
            />
          </div>
          <h3 className={cn('font-semibold', styles.title)}>
            {title}
          </h3>
        </div>

        {/* Main interpretation text */}
        <p className="text-gray-700 text-base leading-relaxed pl-12">
          {interpretation}
        </p>

        {/* Optional tips list */}
        {tips && tips.length > 0 && (
          <div className="pl-12 pt-2 border-t border-gray-200/50">
            <div className="flex items-center gap-2 mb-2">
              {showTipsIcon && (
                <Lightbulb className="h-4 w-4 text-gray-500" aria-hidden="true" />
              )}
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                Quick Tips
              </span>
            </div>
            <ul className="space-y-1.5" role="list">
              {tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-2">
                  <div className="mt-1.5 flex-shrink-0">
                    <div className="h-1.5 w-1.5 rounded-full bg-green-500" aria-hidden="true" />
                  </div>
                  <span className="text-sm text-gray-600 leading-relaxed">
                    {tip}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * Compact variant for inline use within other cards
 */
export function WhatThisMeansInline({
  interpretation,
  className,
}: {
  interpretation: string
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex items-start gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100',
        className
      )}
    >
      <Info className="h-4 w-4 text-gray-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
      <p className="text-sm text-gray-600 leading-relaxed">
        {interpretation}
      </p>
    </div>
  )
}

/**
 * Helper function to generate common interpretations
 */
export const interpretations = {
  /** For watering calculators */
  wateringAmount: (inchesPerWeek: number, sessions: number) =>
    `Your lawn needs approximately ${inchesPerWeek.toFixed(1)} inches of water per week. ` +
    `Split this into ${sessions} watering sessions for best absorption and to prevent runoff.`,

  /** For fertilizer calculators */
  fertilizerAmount: (lbsNitrogen: number, sqft: number) =>
    `Your ${sqft.toLocaleString()} sq ft lawn needs about ${lbsNitrogen.toFixed(1)} lbs of nitrogen per application. ` +
    `This amount promotes healthy growth without risking fertilizer burn.`,

  /** For mowing calculators */
  mowingHeight: (heightInches: number, grassType: string) =>
    `For ${grassType}, maintaining a height of ${heightInches} inches promotes deeper root growth, ` +
    `better drought resistance, and naturally crowds out weeds.`,

  /** For seeding calculators */
  seedTiming: (idealWindow: string, soilTemp: number) =>
    `The best time to seed is during ${idealWindow} when soil temperatures reach ${soilTemp}°F. ` +
    `This gives seeds optimal conditions for germination and establishment.`,

  /** For aeration calculators */
  aerationTiming: (timing: string, grassType: string) =>
    `${timing} is the ideal aeration window for ${grassType}. ` +
    `Aerating during active growth allows your lawn to recover quickly and fill in the holes.`,

  /** For herbicide timing */
  herbicideTiming: (type: 'pre' | 'post', targetTemp: number) =>
    type === 'pre'
      ? `Apply pre-emergent when soil temperatures consistently reach ${targetTemp}°F. ` +
        `This creates a barrier before weed seeds germinate.`
      : `Apply post-emergent herbicide when weeds are actively growing and temperatures are between ${targetTemp}-85°F ` +
        `for best absorption and effectiveness.`,
}

export default WhatThisMeansCard
